// Tratamento do marcador de incerteza [?] (Regra 3). O prompt insere [?]
// IMEDIATAMENTE após o trecho incerto, SEM delimitar o início da frase
// (prompts/soap.md). Logo, do texto só dá para afirmar a POSIÇÃO do token — não a
// extensão da frase incerta. Por isso:
//   - inline: realça apenas o token [?] (segmentar);
//   - painel: mostra uma JANELA DE CONTEXTO ao redor do [?] (trechosIncertos),
//     rotulada como contexto, nunca como "a frase incerta".
// Decisão validada com o piloto — não reconstruir fronteira por heurística.

import { CAMPOS, type CampoSoap, type Soap } from '../api/types';

export const MARCADOR = '[?]';

const LETRA: Record<CampoSoap, 'S' | 'O' | 'A' | 'P'> = {
  subjetivo: 'S',
  objetivo: 'O',
  avaliacao: 'A',
  plano: 'P',
};

export function contarMarcadores(texto: string): number {
  if (!texto) return 0;
  return texto.split(MARCADOR).length - 1;
}

export type Segmento = { tipo: 'texto'; valor: string } | { tipo: 'marcador' };

// Quebra o texto em segmentos alternando trecho comum e o token [?], para o
// render realçar só o marcador. `[?]` é literal (split de string, sem regex).
export function segmentar(texto: string): Segmento[] {
  const partes = texto.split(MARCADOR);
  const segs: Segmento[] = [];
  partes.forEach((p, i) => {
    if (p) segs.push({ tipo: 'texto', valor: p });
    if (i < partes.length - 1) segs.push({ tipo: 'marcador' });
  });
  return segs;
}

export interface TrechoIncerto {
  campo: 'S' | 'O' | 'A' | 'P';
  contexto: string; // janela ao redor do [?], com o próprio marcador
}

const ANTES = 30;
const DEPOIS = 12;

// Janela de contexto ao redor de um [?] em `idx`. Apara nas fronteiras de palavra
// para não cortar um termo clínico no meio; elipse quando corta.
function janela(texto: string, idx: number): string {
  const fimMarcador = idx + MARCADOR.length;
  let ini = Math.max(0, idx - ANTES);
  let fim = Math.min(texto.length, fimMarcador + DEPOIS);
  if (ini > 0) {
    const esp = texto.indexOf(' ', ini);
    if (esp !== -1 && esp < idx) ini = esp + 1;
  }
  if (fim < texto.length) {
    const esp = texto.lastIndexOf(' ', fim);
    if (esp > fimMarcador) fim = esp;
  }
  const prefixo = ini > 0 ? '…' : '';
  const sufixo = fim < texto.length ? '…' : '';
  return prefixo + texto.slice(ini, fim).trim() + sufixo;
}

// Todos os [?] do SOAP, na ordem dos campos, cada um com sua janela de contexto.
export function trechosIncertos(soap: Soap): TrechoIncerto[] {
  const out: TrechoIncerto[] = [];
  for (const k of CAMPOS) {
    const texto = soap[k];
    let from = 0;
    for (;;) {
      const idx = texto.indexOf(MARCADOR, from);
      if (idx === -1) break;
      out.push({ campo: LETRA[k], contexto: janela(texto, idx) });
      from = idx + MARCADOR.length;
    }
  }
  return out;
}
